/**
 * What the account earned over the selected period, and how it traded to earn it.
 *
 * Two strips, both governed by the period control above them and both saying
 * so in their own heading. That is the whole reason they are separate from the
 * account strip: the account is a state true at this moment, these are amounts
 * accumulated over a range, and a screen that puts the two under one control
 * answers a question nobody asked with figures that look identical.
 *
 * Every figure arrives measured from one Analytics snapshot for one exact
 * span. Nothing here divides, sums or converts — a win rate computed in the
 * browser is a second opinion about the ledger.
 */
import { Metric } from "@/design-system/Metric";
import { projectAccountAnalytics } from "@/shared/analytics/presentation";
import type { AccountAnalyticsResponse } from "@/shared/api";
import { ABSENT, percent, ratio } from "@/shared/metric/metric";
import { Sparkline } from "@/design-system/Sparkline";
import { isNegative, money, signedMoney, signedPercent } from "@/shared/money/money";
import { humanAge } from "@/shared/time/age";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";

type Props = {
  readonly analytics: AccountAnalyticsResponse | null;
  readonly label: string;
  readonly loading: boolean;
};

function Strip({
  heading,
  children,
  testId,
  span,
  columns,
}: {
  readonly heading: string;
  readonly children: React.ReactNode;
  readonly testId: string;
  /** Its share of the twelve columns. The two modules are read together. */
  readonly span: string;
  /** How many figures stand across. A fixed grid whose column count divides
   *  the figures exactly: nine across five left a hole in the second row, and
   *  a rectangle with a bite out of it reads as two groups rather than one. */
  readonly columns: string;
}): React.JSX.Element {
  return (
    <section
      data-testid={testId}
      className={`flex min-w-0 flex-col overflow-hidden rounded-panel border border-border-subtle bg-surface-default ${span}`}
    >
      <div className={PANEL_HEADER}>
        <h2 className={PANEL_TITLE}>{heading}</h2>
      </div>
      <div
        className={`grid flex-1 content-start gap-x-6 gap-y-4 p-3 ${columns}`}
      >
        {children}
      </div>
    </section>
  );
}

export function PeriodResult({ analytics, label, loading }: Props): React.JSX.Element {
  const evidence = analytics === null ? null : projectAccountAnalytics(analytics);

  if (evidence === null) {
    return (
      <Strip
        heading={`Результат · ${label}`}
        testId="period-result"
        span="xl:col-span-5"
        columns="grid-cols-2 grid-rows-[auto_auto_1fr]"
      >
        <span className="text-helper-sm text-text-muted" data-testid="period-result-state">
          {loading ? "Читаю период…" : "Результат за период недоступен."}
        </span>
      </Strip>
    );
  }

  const summary = analytics?.summary ?? null;

  // How long the account actually traded inside the period, in the reader's
  // words. A year selected against eight days of record is eight days of
  // result, and the rate has to say so or it reads as a yearly one.
  const traded =
    summary?.traded_seconds == null ? null : humanAge(summary.traded_seconds);

  /**
   * Why a figure is missing, in the place the figure would have been.
   *
   * A dash on its own says "not available" and leaves the reader to guess
   * between "nothing happened", "we cannot compute it" and "the screen is
   * broken". Three different facts, one glyph — so the hint carries the
   * reason whenever the value is absent.
   */
  const missing = (reason: string): string => {
    if (analytics?.stale === true) return "чтение устарело";
    if (analytics?.covered !== false) return reason;
    // Name the date the record actually starts. "Not covered" tells the reader
    // the figure is missing; the date tells them why, and which period would
    // have produced one.
    const since = analytics.coverage_since;
    return since === null
      ? "период не покрыт записью"
      : `запись ведётся с ${new Date(since).toLocaleDateString("ru-RU", {
          day: "numeric",
          month: "short",
        })}`;
  };

  return (
    <>
      <Strip
        heading={`Результат · ${label}`}
        testId="period-result"
        span="xl:col-span-5"
        columns="grid-cols-2 grid-rows-[auto_auto_1fr]"
      >
        <Metric
          metricId="pnl.realised_net"
          role="primary"
          value={evidence.realisedNet}
          display={signedMoney(evidence.realisedNet)}
          directional
          size="display"
          hint={label}
        />
        {/* The deposit rate: what the closed cycles earned on the money the
            owner put in. Read the way a bank quotes one — placed, credited,
            percent — which is why the amount it is measured on stands beside
            it rather than being left implied. */}
        <Metric
          metricId="analytics.owner_return"
          role="context"
          value={evidence.ownerReturn}
          display={
            evidence.ownerReturn === null ? undefined : signedPercent(evidence.ownerReturn)
          }
          directional
          hint={
            evidence.ownerReturn === null
              ? missing("нет вложенного капитала за период")
              : traded === null
                ? undefined
                : `за ${traded} торговли`
          }
        />
        <Metric
          label="База доходности за период"
          testId="period-invested"
          unit="USDT"
          value={summary?.invested ?? null}
          display={summary === null ? undefined : money(summary.invested)}
          hint="вложено за период; пополнение посреди периода учтено по доле времени"
        />
        {/* The same rate over thirty days, so a week and a month can be
            compared with each other and with what a deposit would pay. Absent
            under a week of trading, where it would describe the sample. */}
        <Metric
          label="В месяц"
          testId="period-monthly"
          value={summary?.monthly_return ?? null}
          display={
            summary?.monthly_return == null
              ? undefined
              : `≈ ${signedPercent(summary.monthly_return, 1)}`
          }
          directional
          hint={
            summary?.monthly_return == null
              ? "нужна неделя торговли"
              : "при том же темпе"
          }
        />
        <Metric
          label="Портфельный PnL"
          testId="period-portfolio-pnl"
          unit="USDT"
          value={summary?.portfolio_pnl ?? null}
          display={
            summary?.portfolio_pnl == null ? undefined : signedMoney(summary.portfolio_pnl)
          }
          directional
          hint={
            summary?.portfolio_pnl == null
              ? missing("нет записи equity за период")
              : "движение средств исключено"
          }
        />
        <Metric
          label="Максимальная просадка"
          testId="period-drawdown"
          value={summary?.maximum_drawdown ?? null}
          display={
            summary?.maximum_drawdown == null
              ? undefined
              : percent(summary.maximum_drawdown)
          }
          hint={
            summary?.maximum_drawdown == null
              ? missing("нет записи equity за период")
              : "за период"
          }
        />

        {/* The shape of the period beside its figures. No axes and no labels:
            anyone who needs a value reads the value next to it, and a
            sparkline with a tooltip is a chart that outgrew its slot. */}
        {analytics !== null && analytics.realised.length > 1 && (
          <span className="col-span-full flex max-h-28 min-h-16 w-full items-end self-end">
            <Sparkline
              points={analytics.realised}
              label={`Динамика реализованного NET за ${label}`}
              rising={!isNegative(evidence.realisedNet)}
              testId="period-sparkline"
            />
          </span>
        )}
      </Strip>

      <Strip
        heading={`Эффективность торговли · ${label}`}
        testId="period-stats"
        span="xl:col-span-7"
        columns="grid-cols-2 sm:grid-cols-3"
      >
        {/* The one figure this region is about. A share of winning cycles is
            what an operator checks first, and it was seventh of nine at a
            single weight — which is another way of saying the block had no
            subject. Coloured by outcome, like every other result. */}
        <Metric
          label="Доля прибыльных"
          testId="period-winrate"
          value={summary?.winrate ?? null}
          display={summary?.winrate == null ? undefined : percent(summary.winrate)}
          size="large"
          hint={
            summary === null
              ? undefined
              : `${String(summary.winners)} прибыльных из ${String(evidence.cycles)}`
          }
        />
        <Metric
          label="Завершено циклов"
          testId="period-cycles"
          value={String(evidence.cycles)}
          display={String(evidence.cycles)}
        />
        <Metric
          label="Средний PnL цикла"
          testId="period-average-net"
          unit="USDT"
          value={summary?.average_net ?? null}
          display={summary?.average_net == null ? undefined : signedMoney(summary.average_net)}
          directional
        />
        <Metric
          label="Средний цикл"
          testId="period-average-duration"
          value={summary?.average_seconds == null ? null : String(summary.average_seconds)}
          display={
            summary?.average_seconds == null ? ABSENT : humanAge(summary.average_seconds)
          }
        />
        <Metric
          label="Медианный цикл"
          testId="period-median-duration"
          value={summary?.median_seconds == null ? null : String(summary.median_seconds)}
          // Beside the mean, never instead of it. One long recovery drags an
          // average past every cycle that actually ran, and the gap between
          // the two figures is the thing worth seeing.
          display={summary?.median_seconds == null ? ABSENT : humanAge(summary.median_seconds)}
        />
        <Metric
          label="Доход в день на капитал в сделках"
          testId="period-capital-turnover"
          value={summary?.capital_turnover ?? null}
          // Net per capital-day, as a share: what 100 USDT held in positions
          // earned in a day. «Оборот 0.04 · NET на капитало-день» explained
          // nothing to anybody (owner, 2026-10-31).
          display={percent(summary?.capital_turnover ?? null, 2)}
          hint="сколько приносят 100 USDT в позициях за сутки"
        />
        <Metric
          label="Капитал в сделках, USDT × дни"
          testId="period-capital-days"
          value={summary?.capital_days ?? null}
          // Two places, not twenty-eight. The ledger keeps capital-days at
          // full Decimal precision because it is a sum of exact products; the
          // screen printed all of it, and 280.1586437042396745749384152 is not
          // a figure anybody can read, compare or say out loud.
          display={summary?.capital_days == null ? ABSENT : ratio(summary.capital_days, 2)}
          hint="сколько денег и как долго работало"
        />
        <Metric
          metricId="analytics.fees"
          role="context"
          value={evidence.fees}
          hint={label}
        />
        <Metric
          metricId="analytics.funding"
          role="context"
          value={evidence.funding}
          display={signedMoney(evidence.funding)}
          directional
          hint={label}
        />
      </Strip>
    </>
  );
}
